import { Transcription as TranscriptionSkeleton } from "@circle-react-uikit/SkeletonLoader/skeletons/Transcription";
import { useShouldAutoScroll } from "@circle-react/hooks/utils/useShouldAutoScroll";
import { Badge } from "@circleco/compass/components/Badge";
import { Button } from "@circleco/compass/components/Button";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Popover } from "@circleco/compass/components/Popover";
import { ScrollArea } from "@circleco/compass/components/ScrollArea";
import { SearchInput } from "@circleco/compass/components/SearchInput";
import { Typography } from "@circleco/compass/components/Typography";
import { useCallback, useMemo, useState } from "react";
import type { Cue as WebVTTCue } from "webvtt-parser";

import { t } from "@/i18n-js/instance";

import { useMediaTranscript } from "../useMediaTranscript";
import { Cue } from "./Cue";
import { isCurrentCue, parseCueTime } from "./helpers";

export interface TranscriptPopupProps {
  compact?: boolean;
  cues: WebVTTCue[];
  currentTime: number;
  mediaTranscriptId?: number | null;
  onSeek: (time: number) => void;
  transcriptPreview?: string;
}

export const TranscriptPopup = ({
  onSeek,
  cues: preloadedCues = [],
  currentTime,
  transcriptPreview,
  mediaTranscriptId,
  compact = false,
}: TranscriptPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const cuesContainerRef = useCallback(
    (element: HTMLDivElement | null) => setContainerElement(element),
    [],
  );
  const shouldAutoScroll = useShouldAutoScroll(containerElement);

  const { parsedWebvtt, isLoading } = useMediaTranscript(
    !preloadedCues.length && mediaTranscriptId && isOpen
      ? mediaTranscriptId
      : null,
  );
  const cues = preloadedCues.length
    ? preloadedCues
    : (parsedWebvtt?.cues ?? []);

  const filteredCues = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) return cues;
    return cues.filter(cue =>
      cue.text.toLowerCase().includes(normalizedSearchTerm),
    );
  }, [cues, searchTerm]);

  if (!cues?.length && !mediaTranscriptId) {
    return null;
  }

  const firstCue: WebVTTCue = cues[0] || { text: "", startTime: 0 };
  const previewText = transcriptPreview ?? firstCue.text;
  const triggerLabel = isOpen
    ? t("media_player.hide_transcript")
    : t("media_player.show_transcript");

  const trigger = compact ? (
    <button type="button" className="text-link text-xs hover:underline">
      {triggerLabel}
    </button>
  ) : (
    <Button variant="outline" size="sm" type="button">
      {triggerLabel}
    </Button>
  );

  const popover = (
    <Popover
      trigger={trigger}
      open={isOpen}
      onOpenChange={setIsOpen}
      side="bottom"
      align="start"
      sideOffset={5}
      className="!px-0 !py-3 h-[26rem] w-[25rem] max-w-[calc(100vw-2rem)]"
    >
      <div className="flex h-full flex-col gap-3">
        <div className="flex shrink-0 items-center justify-between px-4">
          <Typography variant="heading-md" color="primary">
            {t("media_player.transcript")}
          </Typography>
          <IconButton
            icon="cross"
            variant="ghost"
            size="sm"
            aria-label={t("close")}
            onClick={() => setIsOpen(false)}
          />
        </div>
        <div className="shrink-0 px-4">
          <SearchInput
            aria-label={t("media_player.search_transcript")}
            placeholder={t("media_player.search_transcript")}
            value={searchTerm}
            onSearch={setSearchTerm}
            onClear={() => setSearchTerm("")}
            rootProps={{ "data-transcript-search-input": true }}
          />
        </div>
        <div className="relative min-h-0 grow overflow-hidden">
          <ScrollArea.Root className="relative size-full" scrollFades>
            <ScrollArea.Viewport ref={cuesContainerRef} className="size-full">
              <ScrollArea.Content>
                <div className="pl-4 pr-2">
                  {isLoading && <TranscriptionSkeleton />}
                  {!isLoading &&
                    filteredCues.map(cue => (
                      <Cue
                        key={`transcript-${cue.startTime}`}
                        cue={cue}
                        container={containerElement}
                        isCurrentCue={isCurrentCue(cue, currentTime)}
                        shouldScroll={shouldAutoScroll && !searchTerm}
                        onSeek={onSeek}
                      />
                    ))}
                  {!isLoading &&
                    filteredCues.length === 0 &&
                    searchTerm.trim() && (
                      <div className="px-4 py-6 text-center text-sm text-tertiary">
                        {t("media_player.no_transcript_results")}
                      </div>
                    )}
                </div>
              </ScrollArea.Content>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              orientation="vertical"
              size="sm"
              visibility="always"
            />
          </ScrollArea.Root>
        </div>
      </div>
    </Popover>
  );

  if (compact) {
    return (
      <blockquote>
        <Typography variant="caption">{previewText}</Typography>
        <div className="flex">{popover}</div>
      </blockquote>
    );
  }

  return (
    <div className="bg-secondary mt-4 flex w-full items-center gap-3 rounded-xl p-3">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Badge
          variant="secondary"
          label={parseCueTime(firstCue.startTime.toString())}
        />
        <div className="min-w-0 flex-1 truncate text-sm text-primary">
          {previewText}
        </div>
      </div>
      <div className="shrink-0 flex items-center">{popover}</div>
    </div>
  );
};
